import { comparer, makeAutoObservable } from 'mobx';

import { LatestRequest, PagedList } from 'framework/api';
import { MobxManager } from 'framework/mobx-integration';
import { iterateRules, RuleDefinitionType } from 'controls/queryBuilder/ruleDefinition';
import { MultiSelectEntry, QueryBuilderProps } from 'controls/queryBuilder/types';
import { isListOperator } from 'controls/queryBuilder/utils/isListOperator';
import { AntSelectOption } from 'controls/react/ant/ant-select/types';

export class QueryBuilderStore {
	props: QueryBuilderProps;

	private listValues = new Map<string, LatestRequest<PagedList<MultiSelectEntry>>>();
	private mobx = new MobxManager();

	constructor() {
		makeAutoObservable(this);
	}

	private get columnsToLoad(): string[] {
		const { config, rule, getListValuesRequest } = this.props;
		if (getListValuesRequest == null || config == null) {
			return [];
		}

		const columns = Array.from(iterateRules(rule))
			.filter((part) => part.type == RuleDefinitionType.Rule && isListOperator(part.properties.operator))
			.map((part) => part.properties.field)
			.filter((column) => config[column]?.fieldSettings.listValues.length == 0 && !this.listValues.has(column));

		return [...new Set(columns)];
	}

	init = () => {
		// whoever shows the builder may build the same configuration anew on every render
		this.mobx.reaction(
			() => this.props.config,
			() => this.listValues.clear(),
			{ equals: comparer.structural },
		);

		this.mobx.reaction(
			() => this.columnsToLoad,
			(columns) => columns.forEach(this.loadListValues),
			{ fireImmediately: true, equals: comparer.structural },
		);
	};

	getListValues(column: string): { loading: boolean; values: MultiSelectEntry[] } | undefined {
		if (this.props?.getListValuesRequest == null) {
			return undefined;
		}

		const request = this.listValues.get(column);
		return {
			loading: request?.loading ?? true,
			values: request?.data?.items ?? [],
		};
	}

	private loadListValues = (column: string) => {
		const request = new LatestRequest<PagedList<MultiSelectEntry>>();
		request.load(this.props.getListValuesRequest({ column }));
		this.listValues.set(column, request);
	};

	destroy = () => {
		this.mobx.destroy();
	};
}
